import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { MemoryHistory } from 'history';
import classNames from 'classnames';
import { useIntl } from 'react-intl';

import applicationActions from 'common/support/applicationActions';

import { pageSelected } from 'actions/pageView';

import textToSpeechManager from 'support/tts/TextToSpeechManager';
import { FocusGroup } from 'components/focus';
import LeftNav from 'components/navigation/LeftNav';
import { FocusSettingsProvider } from 'components/focus/FocusSettingsContext';
import WithLoading from 'components/common/WithLoading';
import HomeRows from 'components/pages/home/HomeRows';
import CarouselContainer from 'components/pages/home/carousel/CarouselContainer';
import DevGraphQLLinks from 'components/pages/home/DevGraphQLLinks';

import { useFocusManager } from 'hooks/focus';
import { useRestoration } from 'hooks/restoration';
import { useCurrentVideo } from 'hooks/video';
import { useImperativeTimer, ImperativeTimer } from 'hooks/timeout';
import { useHomeRowsBackNavigation } from 'hooks/homeRows';
import { isPromoRow } from 'support/rowHelper';

import config from 'config';
import useThrottledCallback from 'hooks/throttledCallback';
import { VideoCtvRedux, KitcenSinkRowItemDataItem } from 'store-types';

const messages = {
  homeTts: {
    id: 'home.tts.surfaceHome',
    description: 'Tts for when the home page is mounted',
  },
  settingsRowTitleTts: {
    id: 'tile.settings.tts.rowTitle',
    description: 'Settings tts for row title',
  },
  leaguesRowTitleTts: {
    id: 'tile.leagues.tts.rowTitle',
    description: 'Leagues tts for row title',
  },
  sportsRowTitleTts: {
    id: 'tile.sports.tts.rowTitle',
    description: 'Sports tts for row title',
  },
  localMarketsRowTitleTts: {
    id: 'tile.localMarkets.tts.rowTitle',
    description: 'Local markets tts for row title',
  },
  showsRowTitleTts: {
    id: 'tile.shows.tts.rowTitle',
    description: 'Shows tts for row title',
  },
};
const RestorationStatus = {
  RESTORED: 'restored',
  STARTED: 'started',
  UNRESTORED: 'unrestored',
};

interface TtsRef {
  current?: { getTtsText: () => string } | null;
}

const useHomeTTS = (nowPlayingRef: TtsRef, footerRef: TtsRef) => {
  const focusManager = useFocusManager();
  const rows = useSelector((state: VideoCtvRedux) => state.kitchenSink.data.rows);
  const intl = useIntl();

  const firstTtsCall = useRef(true);
  useEffect(() => {
    const onFocusChangedListener = (newId: string, prevId: string) => {
      const isRectangleRow = newId.includes('leagues');
      const isCircleRowSports = newId.includes('sports');
      const isCircleRowLocalMarkets = newId.includes('markets');
      const isCircleRowShows = newId.includes('shows');
      const focusedTTS = textToSpeechManager.getTTSText(newId);
      if (
        (!focusedTTS ||
          !focusManager.isFocusAncestor('home-focusable-content')) &&
        !isRectangleRow &&
        !isCircleRowSports &&
        !isCircleRowLocalMarkets &&
        !isCircleRowShows
      )
        return;

      const getRowId = (id: string) => {
        // Check for null id. In an edge case, 'prevId' could be null if user click
        // a media tile before initial focus is set
        if (!id) return null;
        const regex = /\brow-([^-]+)/;
        const match = id.match(regex);
        return match?.[1];
      };

      if (firstTtsCall.current) {
        textToSpeechManager.add(intl.formatMessage(messages.homeTts));
        firstTtsCall.current = false;
      }

      if (newId.includes('firstrow') && !prevId.includes('firstrow')) {
        textToSpeechManager.add(nowPlayingRef.current?.getTtsText());
      }

      const newRowId = getRowId(newId);
      const prevRowId = getRowId(prevId);

      if (newRowId !== prevRowId) {
        if (newRowId === 'settings') {
          textToSpeechManager.add(
            intl.formatMessage(messages.settingsRowTitleTts),
          );
        } else if (isRectangleRow) {
          textToSpeechManager.add(
            intl.formatMessage(messages.leaguesRowTitleTts),
          );
        } else if (isCircleRowSports) {
          textToSpeechManager.add(
            intl.formatMessage(messages.sportsRowTitleTts),
          );
        } else if (isCircleRowLocalMarkets) {
          textToSpeechManager.add(
            intl.formatMessage(messages.localMarketsRowTitleTts),
          );
        } else if (isCircleRowShows) {
          textToSpeechManager.add(
            intl.formatMessage(messages.showsRowTitleTts),
          );
        } else if (!newId.includes('firstrow')) {
          // We intentionally do not speak the row title for the first row
          // because the title is not rendered in the app
          const newRow = rows.find(row => row.id === newRowId);

          // If the title field of promo rows ever becomes useable,
          // we can remove the guard against promo rows
          if (!isPromoRow(newRow) && newRow?.title)
            textToSpeechManager.add(newRow.title);
        }
      }

      textToSpeechManager.add(focusedTTS);

      if (newId.includes('lastrow') && !prevId.includes('lastrow')) {
        textToSpeechManager.add(footerRef.current?.getTtsText());
      }

      textToSpeechManager.speak();
    };
    focusManager.addFocusChangedListener(onFocusChangedListener);
    return () => {
      focusManager.removeFocusChangedListener(onFocusChangedListener);
    };
  }, [focusManager, nowPlayingRef, footerRef, rows, intl]);

  return { enableTTSOnFocus: false };
};

const useHomeRestoration = (isReady?: boolean) => {
  const { restorationState, setRestorationState } = useRestoration('home');
  const focusManager = useFocusManager();

  const restoredFocusId = restorationState?.focusId;

  const [restorationStatus, setRestorationStatus] = useState(
    RestorationStatus.STARTED,
  );

  useEffect(() => {
    if (isReady && restorationStatus === RestorationStatus.STARTED) {
      if (
        restoredFocusId &&
        focusManager.getFocusableElement(restoredFocusId)
      ) {
        focusManager.setCurrentFocus(restoredFocusId);
        setRestorationStatus(RestorationStatus.RESTORED);
      } else {
        setRestorationStatus(RestorationStatus.UNRESTORED);
      }
    }
  }, [focusManager, isReady, restoredFocusId, restorationStatus]);

  useEffect(() => {
    const onFocusChangedListener = () => {
      if (focusManager.isFocusAncestor('home-focusable-content')) {
        setRestorationState({
          ...restorationState,
          focusId: focusManager.currentFocusId as string,
        });
      }
    };
    focusManager.addFocusChangedListener(onFocusChangedListener);
    return () => {
      focusManager.removeFocusChangedListener(onFocusChangedListener);
    };
  }, [focusManager, restorationState, setRestorationState]);

  return [restorationStatus, restoredFocusId];
};

interface HomeError {
  type: string;
  providers?: string[];
  isCarousel?: boolean;
  code?: string;
}
interface HomePageProps {
  onError?: ((error: unknown | HomeError) => void) | null;
}

const HomePage = ({ onError = () => {} }: HomePageProps) => {
  const dispatch = useDispatch();

  const nowPlayingRef = useRef();
  const footerRef = useRef();
  // useHomeTTS must be called before all other focus impacting hooks
  const { enableTTSOnFocus } = useHomeTTS(nowPlayingRef, footerRef);
  const [rowsRef, onBackAction] = useHomeRowsBackNavigation();
  const { restorationState } = useRestoration('home');

  const loading = useSelector((state: VideoCtvRedux) => state.kitchenSink.loading);
  const fullScreenPlayer = useSelector((state: VideoCtvRedux) => state.hero.fullScreenPlayer);
  const auth = useSelector((state: VideoCtvRedux) => state.auth);

  const { push } = useHistory() as MemoryHistory;
  const navigateToPlayer = useCallback(
      (rowId?: string | null, videoId?: string, rowSlug?: string, video?: KitcenSinkRowItemDataItem) => {
            const params = new URLSearchParams();
      if (videoId) params.set('video', videoId);
      if (rowId) params.set('row', rowId);
      if (rowSlug) params.set('rowSlug', rowSlug);
      const search = params.toString();

      if (
        auth.paramountPlus?.user?.data?.userPackage ===
          'CBS_ALL_ACCESS_LOW_COST_PACKAGE' &&
        (auth.adobe?.authN == null || !video?.authTypes?.includes('adobe')) &&
                !video?.authenticationDetails?.paramountplus?.essentials &&
        video?.authenticationDetails?.paramountplus?.premium
      ) {
        // user auth is p+ essentials
        // user is either not logged in with mvpd or (if they are logged in with mvpd) the stream is not mvpd authorized
        // content is p+ premium only
        // show upgrade to watch screen
        push({
          pathname: '/upgradeToWatch',
          ...(search ? { search } : {}),
        });
      } else {
        push({
          pathname: '/playback',
          ...(search ? { search } : {}),
        });
      }
    },
    [push, auth],
  );

  const [rowIndex, setRowIndex] = useState(-1);
  const [carouselFocused, setCarouselFocused] = useState(false);

  const [currentVideo, currentRow] = useCurrentVideo();

  const focusManager = useFocusManager();

  // Handles the initial focus setting logic
  const hasFocusBeenInitialized = useRef(false);
  const [restoredFocus, restoredFocusId] = useHomeRestoration(!loading);

  useEffect(() => {
    dispatch(pageSelected('home', null));
  }, [dispatch]);

  useEffect(() => {
    if (restoredFocusId?.includes('home-carousel')) {
      setCarouselFocused(true);
    } else {
      setCarouselFocused(false);
    }
  }, [restoredFocusId]);

  useEffect(() => {
    if (restoredFocus === RestorationStatus.UNRESTORED) {
      // Make sure video info is available (CSVSTV-570)
      if (!hasFocusBeenInitialized.current && !fullScreenPlayer) {
        focusManager.setCurrentFocus('home-carousel');
        hasFocusBeenInitialized.current = true;
        setCarouselFocused(true);
        rowsRef.current?.resetIndex();
      }
    }
  }, [
    currentRow,
    currentVideo,
    focusManager,
    restoredFocus,
    fullScreenPlayer,
    rowsRef,
  ]);

  useEffect(() => {
    if (restoredFocus === RestorationStatus.RESTORED) {
      hasFocusBeenInitialized.current = true;
    }
  }, [restoredFocus]);

  // Handles row data updating and re-setting focus appropriately
  const rowsLoading = useSelector((state: VideoCtvRedux) => state.kitchenSink.rowsLoading);
  const scoringLoading = useSelector((state: VideoCtvRedux) => state.scoring.loading);
  const idleInterval = useSelector(
    (state: VideoCtvRedux) =>
      state.kitchenSink?.data?.config?.modules?.timeouts?.params
        ?.browseModeTimeout,
  );

  const idleTime = (idleInterval as number) * 1000;

  useEffect(() => {
    if (
      !rowsLoading &&
      !scoringLoading &&
      hasFocusBeenInitialized.current &&
      !fullScreenPlayer
    ) {
      if (carouselFocused) {
        focusManager.setCurrentFocus('home-carousel');
      } else {
        focusManager.setCurrentFocus('home-rows');
      }
    }
  }, [
    focusManager,
    rowsLoading,
    scoringLoading,
    fullScreenPlayer,
    carouselFocused,
  ]);

  const timerRef = useRef<ImperativeTimer>();

  timerRef.current = useImperativeTimer(() => {
    navigateToPlayer();
  }, idleTime || config.playback.idleToFullScreenMs);

  useEffect(() => {
    if (fullScreenPlayer || carouselFocused) {
      timerRef.current?.stop();
    } else {
      timerRef.current?.restart();
    }
  }, [fullScreenPlayer, carouselFocused]);

  useEffect(() => {
    const onFocusChangedListener = () => {
      if (!fullScreenPlayer && !carouselFocused) {
        timerRef.current?.restart();
      }
    };
    focusManager.addFocusChangedListener(onFocusChangedListener);
    return () => {
      focusManager.removeFocusChangedListener(onFocusChangedListener);
    };
  }, [focusManager, fullScreenPlayer, carouselFocused]);

  const onMouseMove = useThrottledCallback<() => void>(() => {
    if (!fullScreenPlayer && !carouselFocused) {
      timerRef.current?.restart();
    }
  }, 500);

  const onTileClick = useCallback(
    tileProps => {
      const { rowId, video, rowSlug } = tileProps;
      navigateToPlayer(rowId, video.id, rowSlug, video);
    },
    [navigateToPlayer],
  );

  const onUpcomingEventClick = useCallback(
    videoId => {
      navigateToPlayer(null, videoId);
    },
    [navigateToPlayer],
  );

  const onHomeRowsFocusIndexChanged = useCallback(index => {
    setRowIndex(index);
    if (index < 0) {
      setCarouselFocused(true);
    } else {
      setCarouselFocused(false);
    }
  }, []);

  const [animate, setAnimate] = useState(true);

  const shouldAnimate = () => {
    if (restorationState?.focusId?.includes('firstrow') && animate) {
      return false;
    }
    return true;
  };

  return (
    <WithLoading loading={loading}>
      <LeftNav focusId="left-nav-focusable-content" />
      <FocusSettingsProvider enableTTSOnFocus={enableTTSOnFocus}>
        {!loading && (
          <FocusGroup
            data-testid="home-page"
            className="w-100 h-100 position-relative"
            focusId="home-focusable-content"
            onActionOverride={action => {
              if (action === applicationActions.back) {
                const focusOnCarousel = rowIndex < 0;
                onBackAction(focusOnCarousel);
                return true;
              }
              if (
                action === applicationActions.moveUp ||
                applicationActions.moveDown
              ) {
                if (rowIndex <= 1) {
                  setAnimate(false);
                }
              }
              return false;
            }}
          >
            <div
              className={classNames(
                'overflow-hidden',
                'background-gradient-page',
                'w-100 h-100',
              )}
              onMouseMove={onMouseMove}
            >
              <DevGraphQLLinks />
              {rowIndex <= 0 && (
                <CarouselContainer
                  id="home-carousel"
                  shouldCleanUp
                  hideCarousel={rowIndex >= 0}
                  shouldAnimate={shouldAnimate()}
                  enableAutoScroll={!fullScreenPlayer}
                  onError={onError}
                  carouselFocused={carouselFocused}
                  onUpcomingEventClick={onUpcomingEventClick}
                />
              )}
              {!fullScreenPlayer && (
                <HomeRows
                  focusId="home-rows"
                  trackingRowOffset={1}
                  onTileClick={onTileClick}
                  onHomeRowsFocusIndexChanged={onHomeRowsFocusIndexChanged}
                  ref={rowsRef}
                />
              )}
            </div>
          </FocusGroup>
        )}
      </FocusSettingsProvider>
    </WithLoading>
  );
};

export default HomePage;
